/**
 * Guarda de permiso de las pantallas de Pedidos de láminas.
 *
 * La ruta ya cuelga de `RequireAuth`; el gating de permiso lo hace la pantalla
 * (mismo patrón que `cutlist/RequireCutlistPermission`). Replica la paridad con el
 * SSR (`docs/frontend_architecture.md`, Pr. 5): el permiso requerido se pasa por
 * prop (el tablero exige `view_pedidos_laminas`; el historial,
 * `view_historial_pedidos_laminas`); sin él se muestra un aviso en vez del
 * contenido. No inventa navegación ni permisos: son los mismos del menú y la API.
 */
import type { ReactNode } from "react";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui";
import { useAuth } from "@/lib/auth";

interface RequirePedidosPermissionProps {
  /** Permiso requerido para ver la pantalla (espejo del menú/SSR/API). */
  permission: string;
  /** Título de la tarjeta de carga / aviso (p. ej. "Pedidos de láminas"). */
  title: string;
  /** Mensaje de "sin permiso". */
  deniedMessage: string;
  children: ReactNode;
}

export function RequirePedidosPermission({
  permission,
  title,
  deniedMessage,
  children,
}: RequirePedidosPermissionProps) {
  const { status, hasPermission } = useAuth();

  // Mientras la sesión se hidrata (`/auth/me`), `hasPermission` aún no conoce los
  // permisos: mostrar "sin permiso" en ese instante sería engañoso. Se espera a
  // tener la sesión resuelta (la ruta ya está protegida por `RequireAuth`).
  if (status === "loading") {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{title}</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-slate-500" role="status">
            Cargando…
          </p>
        </CardContent>
      </Card>
    );
  }

  if (!hasPermission(permission)) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{title}</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-red-600" role="alert">
            {deniedMessage}
          </p>
        </CardContent>
      </Card>
    );
  }

  return <>{children}</>;
}
